import { useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { X, Plus, Loader2, Link2 } from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";

/**
 * Collaboration Opportunities — "Start a collaboration" on The Floor, reused for editing
 * (Phase 2, 2026-08) when `initialOpportunity` is passed in. Visual pass (2026-08) matches the
 * approved mockup's "turn your idea into a team" story: THE IDEA → YOUR ROLE → BUILD YOUR TEAM.
 * Same fields, same validation, same submit calls as before — this pass only restructures the
 * layout. NOT a job posting: no rate/budget/fee field exists anywhere in this form.
 */
const LOCATION_TYPES = ["Remote", "Hybrid", "On-site"];
const PROJECT_TYPES = ["Short Film", "Music Video", "Commercial/Ad", "Documentary", "Wedding", "Brand Content", "Event Coverage", "Personal Project", "Other"];
const COMPENSATION_OPTIONS = [{ value: "unpaid", label: "Unpaid" }, { value: "paid", label: "Paid" }];
const MAX_SLOTS = 10;
const MAX_LINKS = 5;
const DISCLAIMER_TEXT = "Payment is arranged directly by the Member Collaboration's creator. NJM is not a party to the payment.";
const emptySlot = () => ({ slot_id: null, role: "", quantity_required: 1, expertise_niche: "", quantity_filled: 0, fee_amount: "", fee_tbd: false });
const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const label = "font-mont text-[10px] font-semibold uppercase tracking-[0.12em] text-faint";
const input = "mt-1.5 w-full rounded-lg border border-black/10 bg-paper px-3.5 py-2.5 font-mont text-base md:text-sm text-ink placeholder:text-faint focus:outline-none focus:border-ink transition-colors";

function StepHeader({ n, dark, title, hint }) {
  return (
    <div className="flex items-baseline gap-2.5">
      <div className={`h-[22px] w-[22px] shrink-0 rounded-full flex items-center justify-center font-mont text-[10px] font-extrabold ${dark ? "bg-ink text-brand" : "bg-brand text-ink"}`}>{n}</div>
      <span className="font-mont text-[10px] font-extrabold uppercase tracking-[0.16em] text-ink">{title}</span>
      {hint && <span className="font-mont text-[10.5px] text-faint">{hint}</span>}
    </div>
  );
}

export function CreateCollaborationModal({ roleOptions, cityOptions, initialOpportunity, onClose, onSaved }) {
  const { user } = useAuth();
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, true, onClose);
  const isEdit = Boolean(initialOpportunity);
  const [projectName, setProjectName] = useState(initialOpportunity?.project_name || "");
  const [description, setDescription] = useState(initialOpportunity?.description || "");
  const [city, setCity] = useState(initialOpportunity?.city || "");
  const [locationType, setLocationType] = useState(initialOpportunity?.location_type || "");
  const [projectType, setProjectType] = useState(initialOpportunity?.project_type || "");
  const [timeline, setTimeline] = useState(initialOpportunity?.timeline || "");
  const [creatorRole, setCreatorRole] = useState(initialOpportunity?.creator_role || "");
  const [compensation, setCompensation] = useState(initialOpportunity?.compensation === "paid" ? "paid" : "unpaid");
  const [slots, setSlots] = useState(
    initialOpportunity?.role_slots?.length
      ? initialOpportunity.role_slots.map((s) => ({ slot_id: s.slot_id, role: s.role, quantity_required: s.quantity_required, expertise_niche: s.expertise_niche || "", quantity_filled: s.quantity_filled || 0, fee_amount: s.fee_amount || "", fee_tbd: Boolean(s.fee_tbd) }))
      : [emptySlot()]
  );
  const initialFeeMap = (initialOpportunity?.role_slots || []).reduce((m, s) => { m[s.slot_id] = `${s.fee_amount || ""}|${s.fee_tbd ? 1 : 0}`; return m; }, {});
  const [links, setLinks] = useState(initialOpportunity?.inspiration_links || []);
  const [linkDraft, setLinkDraft] = useState("");
  const [notes, setNotes] = useState(initialOpportunity?.additional_notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [nicheSuggestions, setNicheSuggestions] = useState([]);
  const guard = useEmailGuard();
  const creatorName = initialOpportunity?.creator_name || user?.name || "";

  useEffect(() => {
    api.get("/v1/collaboration/niche-suggestions").then((r) => setNicheSuggestions(r.data.items || [])).catch(() => {});
  }, []);

  const validate = () => {
    const e = {};
    if (!projectName.trim()) e.projectName = "Required";
    else if (projectName.length > 120) e.projectName = "Keep it under 120 characters";
    if (!description.trim()) e.description = "Required";
    else if (description.length > 2000) e.description = "Keep it under 2000 characters";
    if (!creatorRole) e.creatorRole = "Pick your role";
    if (slots.filter((s) => s.role && s.quantity_required >= 1).length === 0) e.slots = "Add at least one role you're looking for";
    if (timeline.length > 120) e.timeline = "Keep it under 120 characters";
    if (notes.length > 1000) e.notes = "Keep it under 1000 characters";
    if (links.length > MAX_LINKS) e.links = `Up to ${MAX_LINKS} links`;
    if (compensation === "paid") {
      const validSlots = slots.filter((s) => s.role && s.quantity_required >= 1);
      if (validSlots.some((s) => !s.fee_tbd && !s.fee_amount)) e.fees = "Set a fee (INR) or mark \"Fee to be discussed\" for every role";
    }
    return e;
  };

  const updateSlot = (i, patch) => setSlots(slots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addSlot = () => slots.length < MAX_SLOTS && setSlots([...slots, emptySlot()]);
  const removeSlot = (i) => {
    if (slots[i].quantity_filled > 0) {
      toast.error("Can't remove — this role already has accepted members");
      return;
    }
    setSlots(slots.filter((_, idx) => idx !== i));
  };
  const addLink = () => {
    const v = linkDraft.trim();
    if (v && links.length < MAX_LINKS) setLinks([...links, v]);
    setLinkDraft("");
  };

  const teamSize = slots.filter((s) => s.role).reduce((sum, s) => sum + Number(s.quantity_required || 0), 0);

  // Wave 2 (Nikhil, 2026-09-30) — "confirm, don't block" guard when a creator flips Paid ->
  // Unpaid or edits a fee while there are already pending/accepted applicants.
  const compensationChanged = isEdit && (initialOpportunity.compensation === "paid" ? "paid" : "unpaid") !== compensation;
  const feesChanged = slots.some((s) => s.slot_id && initialFeeMap[s.slot_id] !== undefined && initialFeeMap[s.slot_id] !== `${s.fee_amount || ""}|${s.fee_tbd ? 1 : 0}`);
  const showFeeChangeWarning = isEdit && initialOpportunity?.has_active_applications && (compensationChanged || feesChanged);

  const submit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Fix the highlighted fields");
      return;
    }
    setErrors({});
    const validSlots = slots.filter((s) => s.role && s.quantity_required >= 1);
    const payload = {
      project_name: projectName.trim(), description: description.trim(),
      city: city || null, location_type: locationType || null, project_type: projectType || null,
      timeline: timeline.trim() || null, creator_role: creatorRole, compensation,
      role_slots: validSlots.map((s) => ({
        ...(s.slot_id ? { slot_id: s.slot_id } : {}),
        role: s.role, quantity_required: Number(s.quantity_required), expertise_niche: s.expertise_niche.trim() || null,
        fee_amount: compensation === "paid" && !s.fee_tbd && s.fee_amount ? Number(s.fee_amount) : null,
        fee_tbd: compensation === "paid" ? Boolean(s.fee_tbd) : false,
      })),
      inspiration_links: links, additional_notes: notes.trim() || null,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/v1/collaboration/opportunities/${initialOpportunity.id}`, payload);
        toast.success("Member Collaboration updated");
      } else {
        // Email Send Guard (2026-02, H2 fix — Cursor review of 90c6647): members/freelancers
        // never trigger the eligible-freelancer email blast directly or via confirmation —
        // the opportunity itself is always created immediately; `email_pending_approval` on
        // the response means a founder/partner/pm must review+confirm it before anything
        // sends (never a recipient list, preview, or token in this response for a member).
        const r = await api.post("/v1/collaboration/opportunities", payload);
        if (r.data?.opportunity?.email_pending_approval) {
          toast.success("Member Collaboration posted to The Floor", {
            description: "Email pending approval — NJM will review before notifying members.",
          });
        } else if (guard.intercept(r.data, async (typedCount) => {
          await api.post("/v1/collaboration/opportunities", { ...payload, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
          toast.success("Member Collaboration posted to The Floor");
          onSaved();
        })) {
          return;
        } else {
          toast.success("Member Collaboration posted to The Floor");
        }
      }
      onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      if (detail.code === "invalid_role") toast.error("Pick a role from the list for every field");
      else if (detail.code === "too_many_slots") toast.error(`Up to ${MAX_SLOTS} roles per opportunity`);
      else if (detail.code === "field_too_long") setErrors({ [detail.field === "additional_notes" ? "notes" : detail.field]: `Keep it under ${detail.max} characters` });
      else if (detail.code === "too_many_links") setErrors({ links: `Up to ${detail.max} links` });
      else if (detail.code === "slot_below_accepted") toast.error("That role already has accepted members — quantity can't go below that");
      else if (detail.code === "slot_has_applications") toast.error("That role already has applicants — remove is blocked to protect them");
      else if (detail.code === "invalid_project_type") toast.error("Pick a project type from the list");
      else if (detail.code === "member_collaboration_creator_not_approved") toast.error("Only approved, onboarded NJM Build members can start a Member Collaboration");
      else if (detail.code === "member_collaboration_no_freelancer_profile") toast.error(detail.message || "We couldn't find a freelancer profile for this login. Sign in with your NJM Build (freelancer) account instead.");
      else if (detail.code === "member_collaboration_not_onboarded") toast.error(detail.message || "Your NJM Build profile isn't marked Onboarded yet. Contact NJM to confirm your onboarding status.");
      else if (detail.code === "staff_cannot_create_member_collaboration") toast.error("Staff accounts can't start a Member Collaboration");
      else if (detail.code === "fee_required_for_paid_role") toast.error(`Set a fee or "Fee to be discussed" for ${detail.role || "every role"}`);
      else if (detail.code === "invalid_fee_amount") toast.error("That fee amount doesn't look right");
      else if (detail.code === "confirmation_required") toast.error("That confirmation expired — try posting again");
      else toast.error(isEdit ? "Couldn't save changes" : "Couldn't post this opportunity");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose} data-testid="create-collaboration-overlay">
      <datalist id="collab-niche-suggestions">
        {nicheSuggestions.map((n) => <option key={n} value={n} />)}
      </datalist>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={isEdit ? "Edit collaboration" : "Start a collaboration"} tabIndex={-1} className="w-full max-w-2xl rounded-2xl bg-paper shadow-2xl max-h-[88vh] flex flex-col overflow-hidden [&_button]:min-h-[44px] [&_button]:min-w-[44px] [&_input:not([type=checkbox])]:text-base [&_textarea]:text-base [&_select]:text-base md:[&_input:not([type=checkbox])]:text-sm md:[&_textarea]:text-sm md:[&_select]:text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 bg-ink border-t-[3px] border-brand px-6 sm:px-7 py-5 flex items-start justify-between">
          <div>
            <div className="font-mont text-[9.5px] font-extrabold uppercase tracking-[0.16em] text-brand">{isEdit ? "Edit Member Collaboration" : "Start A Member Collaboration"}</div>
            <div className="mt-1.5 font-mont text-lg font-bold text-paper">{isEdit ? "Update your idea" : "Turn your idea into a team"}</div>
          </div>
          <button onClick={onClose} aria-label="Close collaboration" data-testid="create-collaboration-close" className="min-h-[44px] min-w-[44px] text-paper/60 hover:text-paper transition-colors text-lg leading-none">✕</button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* THE IDEA */}
          <div className="px-6 sm:px-7 pt-6">
            <StepHeader n={1} title="The Idea" />
            <div className="mt-4 ml-8 space-y-4">
              <div>
                <label className={label}>Project Name</label>
                <input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. Short Film — Monsoon" data-testid="create-collaboration-project-name"
                  className={`${input} font-bold text-base ${errors.projectName ? "border-red-400" : ""}`} />
                {errors.projectName && <div className="mt-1 text-[11px] text-red-500" data-testid="create-collaboration-project-name-error">{errors.projectName}</div>}
              </div>
              <div>
                <label className={label}>What are you making?</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="The vibe, the story, what you need help with." data-testid="create-collaboration-description"
                  className={`${input} resize-none ${errors.description ? "border-red-400" : ""}`} />
                {errors.description && <div className="mt-1 text-[11px] text-red-500" data-testid="create-collaboration-description-error">{errors.description}</div>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={label}>City</label>
                  <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="create-collaboration-city" className={`${input} font-semibold cursor-pointer`}>
                    <option value="">Not specified</option>
                    {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Type</label>
                  <select value={locationType} onChange={(e) => setLocationType(e.target.value)} data-testid="create-collaboration-location-type" className={`${input} font-semibold cursor-pointer`}>
                    <option value="">Not specified</option>
                    {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Timeline <span className="text-hairline-strong">optional</span></label>
                  <input value={timeline} onChange={(e) => setTimeline(e.target.value)} placeholder="Shoot in 3 weeks" data-testid="create-collaboration-timeline" className={`${input} font-semibold ${errors.timeline ? "border-red-400" : ""}`} />
                </div>
              </div>
              {errors.timeline && <div className="text-[11px] text-red-500 -mt-2">{errors.timeline}</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={label}>Project Type <span className="text-hairline-strong">optional</span></label>
                  <select value={projectType} onChange={(e) => setProjectType(e.target.value)} data-testid="create-collaboration-project-type" className={`${input} font-semibold cursor-pointer`}>
                    <option value="">Not specified</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Compensation</label>
                  <select value={compensation} onChange={(e) => setCompensation(e.target.value)} data-testid="create-collaboration-compensation" className={`${input} font-semibold cursor-pointer`}>
                    {COMPENSATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-6 sm:mx-7 mt-6 h-px bg-black/8" />

          {/* YOUR ROLE */}
          <div className="px-6 sm:px-7 pt-5">
            <StepHeader n={2} title="Your Role" />
            <div className={`mt-3.5 ml-8 rounded-xl border border-[#FFE9A8] bg-[#FFFCF2] p-4 flex items-center gap-3.5 ${errors.creatorRole ? "ring-1 ring-red-300" : ""}`}>
              <div className="h-10 w-10 shrink-0 rounded-full bg-brand text-ink flex items-center justify-center font-mont font-bold text-xs">{initials(creatorName)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-mont text-[13.5px] font-bold text-ink">{creatorName || "You"} <span className="ml-1.5 font-mont text-[10px] font-semibold tracking-wider text-[#8A6A00]">YOU · CREATOR</span></div>
                <div className="mt-0.5 font-mont text-[11.5px] text-[#7A6A45]">You're first on this team. What are you doing on this project?</div>
              </div>
              <select value={creatorRole} onChange={(e) => setCreatorRole(e.target.value)} data-testid="create-collaboration-creator-role"
                className="shrink-0 rounded-lg border border-black/15 bg-paper px-3 py-2 font-mont text-xs font-bold text-ink focus:outline-none cursor-pointer max-w-[160px]">
                <option value="">Select role</option>
                {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {errors.creatorRole && <div className="mt-1 ml-8 text-[11px] text-red-500" data-testid="create-collaboration-creator-role-error">{errors.creatorRole}</div>}
          </div>

          <div className="mx-6 sm:mx-7 mt-6 h-px bg-black/8" />

          {/* BUILD YOUR TEAM */}
          <div className="px-6 sm:px-7 pt-5">
            <StepHeader n={3} dark title="Build Your Team" hint="Who are you looking for?" />
            <div className="mt-3.5 ml-8 space-y-2.5">
              {slots.map((s, i) => (
                <div key={i} className="rounded-xl border border-black/10 border-l-[3px] border-l-brand bg-paper p-3.5" data-testid={`create-collaboration-slot-${i}`}>
                  <div className="flex items-center gap-3.5">
                    <div className="h-[34px] w-[34px] shrink-0 rounded-full border-[1.5px] border-dashed border-black/25" />
                    <select value={s.role} onChange={(e) => updateSlot(i, { role: e.target.value })} data-testid={`create-collaboration-slot-role-${i}`}
                      className="flex-1 min-w-0 font-mont text-sm font-bold text-ink bg-transparent focus:outline-none cursor-pointer">
                      <option value="">Select a role</option>
                      {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {slots.length > 1 && (
                      <button onClick={() => removeSlot(i)} data-testid={`create-collaboration-slot-remove-${i}`} className="shrink-0 font-mont text-[11px] font-semibold text-red-500 hover:text-red-600">Remove</button>
                    )}
                  </div>
                  <div className="mt-2.5 ml-[46px] flex flex-wrap items-center gap-2">
                    <select value={s.quantity_required} onChange={(e) => updateSlot(i, { quantity_required: Number(e.target.value) })} data-testid={`create-collaboration-slot-qty-${i}`}
                      className="rounded-full border border-black/12 bg-surface2/60 px-3 py-1.5 font-mont text-[10.5px] font-semibold text-ink-soft focus:outline-none cursor-pointer">
                      {Array.from({ length: 20 }).map((_, n) => n + 1).filter((n) => n >= (s.quantity_filled || 1)).map((n) => <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>)}
                    </select>
                    <input value={s.expertise_niche} onChange={(e) => updateSlot(i, { expertise_niche: e.target.value })} placeholder="+ Niche" list="collab-niche-suggestions" data-testid={`create-collaboration-slot-niche-${i}`}
                      className="rounded-full border border-dashed border-black/16 bg-transparent px-3 py-1.5 font-mont text-[10.5px] text-faint placeholder:text-faint focus:outline-none focus:border-black/40 w-32" />
                  </div>
                  {compensation === "paid" && (
                    <div className="mt-2.5 ml-[46px] flex flex-wrap items-center gap-2" data-testid={`create-collaboration-slot-fee-${i}`}>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mont text-[11px] text-faint">₹</span>
                        <input type="number" min="1" value={s.fee_amount} disabled={s.fee_tbd} onChange={(e) => updateSlot(i, { fee_amount: e.target.value })} placeholder="Fee per person" data-testid={`create-collaboration-slot-fee-amount-${i}`}
                          className="w-36 rounded-full border border-black/12 bg-paper pl-6 pr-3 py-1.5 font-mont text-[10.5px] font-semibold text-ink placeholder:text-faint focus:outline-none focus:border-ink disabled:opacity-50 disabled:bg-surface2/40" />
                      </div>
                      <label className="inline-flex items-center gap-1.5 font-mont text-[10.5px] font-medium text-ink-soft cursor-pointer">
                        <input type="checkbox" checked={s.fee_tbd} onChange={(e) => updateSlot(i, { fee_tbd: e.target.checked, fee_amount: e.target.checked ? "" : s.fee_amount })} data-testid={`create-collaboration-slot-fee-tbd-${i}`} className="accent-brand" />
                        Fee to be discussed
                      </label>
                    </div>
                  )}
                </div>
              ))}
              {slots.length < MAX_SLOTS && (
                <button onClick={addSlot} data-testid="create-collaboration-slot-add"
                  className="w-full rounded-xl border border-dashed border-black/20 bg-surface2/40 py-3 font-mont text-xs font-bold text-ink hover:bg-surface2/70 transition-colors">
                  +  Add another role
                </button>
              )}
            </div>
            {errors.slots && <div className="mt-1.5 ml-8 text-[11px] text-red-500" data-testid="create-collaboration-slots-error">{errors.slots}</div>}
            {errors.fees && <div className="mt-1.5 ml-8 text-[11px] text-red-500" data-testid="create-collaboration-fees-error">{errors.fees}</div>}
            {teamSize > 0 && (
              <div className="mt-3 ml-8 font-mont text-[11px] font-semibold text-faint">Your team will read: <span className="text-ink">You + {teamSize} {teamSize === 1 ? "person" : "people"}</span></div>
            )}
          </div>

          <div className="mx-6 sm:mx-7 mt-6 h-px bg-black/8" />

          {/* References + notes */}
          <div className="px-6 sm:px-7 pt-5 pb-6 space-y-4">
            <div>
              <label className={label}>References <span className="text-hairline-strong">optional</span></label>
              <div className="mt-2 flex items-center gap-2">
                <input value={linkDraft} onChange={(e) => setLinkDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }} placeholder="Paste a video / moodboard link" data-testid="create-collaboration-link-input"
                  className="flex-1 rounded-lg border border-dashed border-black/18 bg-paper px-3.5 py-2.5 font-mont text-xs text-ink placeholder:text-faint focus:outline-none focus:border-ink/40" />
                <button onClick={addLink} data-testid="create-collaboration-link-add" className="rounded-lg border border-black/18 px-4 py-2.5 font-mont text-xs font-bold text-ink hover:border-ink/40">Add</button>
              </div>
              {errors.links && <div className="mt-1 text-[11px] text-red-500">{errors.links}</div>}
              {links.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="create-collaboration-links-list">
                  {links.map((l, i) => (
                    <div key={i} className="flex items-center gap-2.5 rounded-lg border border-black/12 bg-surface2/40 px-3 py-2">
                      <div className="h-8 w-11 shrink-0 rounded bg-hairline flex items-center justify-center"><Link2 className="h-3.5 w-3.5 text-faint" /></div>
                      <span className="flex-1 min-w-0 truncate font-mont text-[11.5px] font-semibold text-ink">{l}</span>
                      <button onClick={() => setLinks(links.filter((_, idx) => idx !== i))} data-testid={`create-collaboration-link-remove-${i}`} className="text-faint hover:text-ink shrink-0"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={label}>Anything else? <span className="text-hairline-strong">optional</span></label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Credit terms, gear you already have, anything applicants should know…" data-testid="create-collaboration-notes"
                className={`${input} resize-none ${errors.notes ? "border-red-400" : ""}`} />
              {errors.notes && <div className="mt-1 text-[11px] text-red-500">{errors.notes}</div>}
            </div>

            <div className="rounded-lg bg-surface2/60 px-3.5 py-3 font-mont text-[11px] leading-relaxed text-faint">
              <span className="font-bold text-ink-soft">About NJM Build Collaborations.</span> Peer-led creative collaborations between NJM Build members. NJM Productions does not manage payments, commercial terms or project execution.
            </div>
            {compensation === "paid" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 font-mont text-[11px] leading-relaxed text-amber-800" data-testid="create-collaboration-disclaimer">
                {DISCLAIMER_TEXT}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-3 border-t border-black/8 bg-surface2/40 px-6 sm:px-7 py-4">
          <button onClick={onClose} data-testid="create-collaboration-cancel" className="min-h-[44px] min-w-[44px] font-mont text-xs font-semibold text-ink-soft hover:text-ink transition-colors">Cancel</button>
          {showFeeChangeWarning ? (
            <ConfirmDialog
              testIdPrefix="create-collaboration-submit"
              title="Save these changes?"
              description="This opportunity already has pending or accepted applicants. Changing pay or fees now won't automatically notify them — you may want to message them directly."
              confirmLabel="Save Changes"
              onConfirm={submit}
              trigger={
                <button disabled={submitting} data-testid="create-collaboration-submit"
                  className="min-h-[44px] inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 font-mont text-xs font-bold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
                  {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save Changes
                </button>
              }
            />
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="create-collaboration-submit"
              className="min-h-[44px] inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 font-mont text-xs font-bold text-ink hover:bg-brand/90 transition-colors disabled:opacity-50">
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} {isEdit ? "Save Changes" : "Start the Member Collaboration →"}
            </button>
          )}
        </div>
      </div>
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}
